import { css, html, LitElement } from "lit";
import { customElement } from "lit/decorators/custom-element.js";
import { property } from "lit/decorators/property.js";
import { sharedStyles } from "../styles/shared";

export type MenuAlign = "top" | "bottom";
export type MenuJustify = "center" | "left" | "right" | "center";

@customElement("bs-menu-content")
export class BsMenuContent extends LitElement {
	@property({ type: String, reflect: true }) align: MenuAlign = "bottom";
	@property({ type: String, reflect: true }) justify: MenuJustify = "right";

	static get styles() {
		return [
			sharedStyles,
			css`
				:host {
					display: inline-flex;
					justify-content: center;
					align-items: center;
					flex-direction: column;
					width: var(--menu-width, max-content);
					max-width: min(var(--menu-max-width-adjust, 70vw), var(--menu-max-width, 70vw));
					max-height: min(var(--menu-max-height-adjust, 70vh), var(--menu-max-height, 70vh));
					transform: var(--menu-transform) translateY(0);
					pointer-events: var(--menu-content-pointer-events, inherit);
					z-index: 12345678;
				}

				#content {
					flex-direction: column;
					display: flex;
					background: var(--menu-content-bg, var(--dark));
					color: var(--menu-content-color, var(--light));
					border-radius: var(--border-radius-l);
					border: var(--menu-content-border, var(--border));
					padding: var(--menu-content-padding, var(--space-s) 0);
					max-width: var(--menu-content-max-width, 100%);
					--markdown-link-color: var(--menu-content-link-color, var(--light));
					width: 100%;
					overflow-y: auto;
					overflow-x: hidden;
					flex-grow: 1;
				}

				:host([align="top"]) #triangle {
					border-bottom: unset;
					border-top: var(--menu-triangle-size, 5px) solid
						var(--menu-triangle-color, var(--menu-content-bg, var(--dark)));
				}

				:host([justify="right"]) {
					justify-content: flex-start;
				}

				:host([align="top"]) {
					bottom: 100%;
					top: unset;
					flex-direction: column-reverse;
				}

				:host([justify="left"]) {
					justify-content: flex-end;
				}

				:host([justify="right"]) {
					align-items: flex-start;
					--menu-transform: translateX(
						calc((var(--menu-triangle-offset, 20px) - var(--menu-triangle-size, 5px) / 2) * -1)
					);
				}

				:host([justify="left"]) {
					align-items: flex-end;
					--menu-transform: translateX(calc(var(--menu-triangle-offset, 20px) - var(--menu-triangle-size, 5px) / 2));
				}

				#triangle {
					width: 0;
					height: 0;
					border-left: var(--menu-triangle-size, 5px) solid transparent;
					border-right: var(--menu-triangle-size, 5px) solid transparent;
					border-bottom: var(--menu-triangle-size, 5px) solid var(--menu-content-bg, var(--dark));
					margin: 0 var(--menu-triangle-offset, 20px);
					flex-shrink: 0;
					pointer-events: none;
				}

				/* Make the menu visible even when slightly outside the menu */
				:host::before {
					display: block;
					content: "";
					position: absolute;
					inset: calc(-1 * var(--menu-content-click-area-offset, 10px));
					pointer-events: var(--menu-content-click-area-pointer-events, var(--menu-content-pointer-events, inherit));
					background: transparent;
					z-index: -1;
				}
			`
		];
	}

	renderContent() {
		return html`<slot></slot>`;
	}

	render() {
		return html`
			<div id="triangle"></div>
			<div id="content">${this.renderContent()}</div>
		`;
	}
}
